/** Colores disponibles para los marcadores del plano. */
export const COLORES_MARCADOR = ['verde', 'naranja', 'rojo', 'azul'] as const;
export type ColorMarcador = (typeof COLORES_MARCADOR)[number];

/** Los colores se guardan en inglés por compatibilidad con lo ya almacenado. */
const A_ALMACENAMIENTO: Readonly<Record<ColorMarcador, string>> = {
  verde: 'green',
  naranja: 'orange',
  rojo: 'red',
  azul: 'blue',
};

const DESDE_ALMACENAMIENTO: Readonly<Record<string, ColorMarcador>> = {
  green: 'verde',
  orange: 'naranja',
  red: 'rojo',
  blue: 'azul',
};

const TOKEN_COLOR: Readonly<Record<ColorMarcador, string>> = {
  verde: '--status-success',
  naranja: '--status-warning',
  rojo: '--status-danger',
  azul: '--status-info',
};

export function colorMarcadorCss(color: ColorMarcador): string {
  return `var(${TOKEN_COLOR[color]})`;
}

export function colorMarcadorAAlmacenamiento(color: ColorMarcador): string {
  return A_ALMACENAMIENTO[color];
}

export function colorMarcadorDesdeAlmacenamiento(valor: unknown): ColorMarcador {
  return DESDE_ALMACENAMIENTO[String(valor ?? '').toLowerCase()] ?? 'verde';
}

/**
 * Marcador colocado sobre el plano de la instalación.
 * `x` e `y` son porcentajes del ancho y alto, de modo que el marcador
 * conserva su sitio aunque la imagen se muestre a otro tamaño.
 */
export interface Marcador {
  id: string;
  x: number;
  y: number;
  color: ColorMarcador;
  sensorId: number | null;
  sensorNombre: string;
  sensorTipo: string;
  sensorArea: string;
  sensorUnidad: string;
}

/** Tarjeta numérica asociada a un sensor. */
export interface TarjetaValor {
  id: string;
  etiqueta: string;
  descripcion: string;
  sensorId: number | null;
  unidad: string;
  tipo: string;
  area: string;
}

export interface PanelPersonalProps {
  usuarioId: string;
  nombreInstalacion: string;
  imagenUrl: string | null;
  marcadores: readonly Marcador[];
  tarjetas: readonly TarjetaValor[];
}

/** Genera un identificador local para un elemento recién añadido. */
function nuevoId(prefijo: string): string {
  return `${prefijo}_${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Panel personal de un usuario: un plano de la instalación con marcadores
 * sobre los sensores y tarjetas con sus valores.
 *
 * La entidad es inmutable: cada cambio devuelve un panel nuevo. Así la pantalla
 * puede deshacer, comparar o descartar sin que el estado quede a medias.
 */
export class PanelPersonal {
  constructor(private readonly props: PanelPersonalProps) {}

  static vacio(usuarioId: string): PanelPersonal {
    return new PanelPersonal({
      usuarioId,
      nombreInstalacion: 'Mi instalación',
      imagenUrl: null,
      marcadores: [],
      tarjetas: [],
    });
  }

  get usuarioId(): string {
    return this.props.usuarioId;
  }
  get nombreInstalacion(): string {
    return this.props.nombreInstalacion;
  }
  get imagenUrl(): string | null {
    return this.props.imagenUrl;
  }
  get marcadores(): readonly Marcador[] {
    return this.props.marcadores;
  }
  get tarjetas(): readonly TarjetaValor[] {
    return this.props.tarjetas;
  }

  get estaVacio(): boolean {
    return (
      this.props.imagenUrl === null &&
      this.props.marcadores.length === 0 &&
      this.props.tarjetas.length === 0
    );
  }

  /** Sensores a los que este panel hace referencia. */
  get sensoresReferenciados(): number[] {
    const ids = [
      ...this.props.marcadores.map((m) => m.sensorId),
      ...this.props.tarjetas.map((t) => t.sensorId),
    ];
    return [...new Set(ids.filter((id): id is number => id !== null))];
  }

  conNombre(nombreInstalacion: string): PanelPersonal {
    return new PanelPersonal({ ...this.props, nombreInstalacion });
  }

  conImagen(imagenUrl: string | null): PanelPersonal {
    return new PanelPersonal({ ...this.props, imagenUrl });
  }

  /** Añade un marcador en el centro del plano, listo para arrastrarlo. */
  agregarMarcador(color: ColorMarcador = 'verde'): PanelPersonal {
    const marcador: Marcador = {
      id: nuevoId('m'),
      x: 50,
      y: 50,
      color,
      sensorId: null,
      sensorNombre: 'Nuevo marcador',
      sensorTipo: '',
      sensorArea: '',
      sensorUnidad: '',
    };
    return new PanelPersonal({ ...this.props, marcadores: [...this.props.marcadores, marcador] });
  }

  actualizarMarcador(id: string, cambios: Partial<Marcador>): PanelPersonal {
    return new PanelPersonal({
      ...this.props,
      marcadores: this.props.marcadores.map((m) => (m.id === id ? { ...m, ...cambios } : m)),
    });
  }

  quitarMarcador(id: string): PanelPersonal {
    return new PanelPersonal({
      ...this.props,
      marcadores: this.props.marcadores.filter((m) => m.id !== id),
    });
  }

  agregarTarjeta(): PanelPersonal {
    const tarjeta: TarjetaValor = {
      id: nuevoId('t'),
      etiqueta: 'Nueva tarjeta',
      descripcion: 'Elige un sensor',
      sensorId: null,
      unidad: '',
      tipo: '',
      area: '',
    };
    return new PanelPersonal({ ...this.props, tarjetas: [...this.props.tarjetas, tarjeta] });
  }

  actualizarTarjeta(id: string, cambios: Partial<TarjetaValor>): PanelPersonal {
    return new PanelPersonal({
      ...this.props,
      tarjetas: this.props.tarjetas.map((t) => (t.id === id ? { ...t, ...cambios } : t)),
    });
  }

  quitarTarjeta(id: string): PanelPersonal {
    return new PanelPersonal({
      ...this.props,
      tarjetas: this.props.tarjetas.filter((t) => t.id !== id),
    });
  }
}
